import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const ResourceSelector = ({ 
  resources, 
  selectedResource, 
  onSelect, 
  type,
  groupBy = 'provider' 
}) => {
  const groupedResources = {};
  
  resources.forEach(resource => {
    const group = resource[groupBy] || 'Other';
    if (!groupedResources[group]) {
      groupedResources[group] = [];
    }
    groupedResources[group].push(resource);
  });

  const getTypeColor = (type) => {
    const colors = {
      model: 'blue',
      embedding: 'purple',
      'vector-db': 'green'
    };
    return colors[type] || 'gray';
  };

  const color = getTypeColor(type);

  const [isOpen, setIsOpen] = React.useState(false);

  const onClose = () => setIsOpen(false);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity ${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {Object.entries(groupedResources).map(([provider, items]) => (
            <div key={provider} className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                {provider}
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  ({items.length} {type}{items.length !== 1 ? 's' : ''})
                </span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((resource, index) => {
                  const isSelected = selectedResource?.name === resource.name && 
                                    selectedResource?.provider === resource.provider;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => onSelect(resource)}
                      className={`text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? `border-${color}-500 bg-${color}-50`
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate">
                            {resource.name}
                          </h5>
                          <div className="mt-2 space-y-1">
                            {resource.temperature !== undefined && (
                              <div className="text-xs text-gray-600">
                                <span className="text-gray-500">Temp:</span>{' '}
                                <span className="font-medium">{resource.temperature}</span>
                              </div>
                            )}
                            {resource.collection_name && (
                              <div className="text-xs text-gray-600">
                                <span className="text-gray-500">Collection:</span>{' '}
                                <span className="font-medium">{resource.collection_name}</span>
                              </div>
                            )}
                            {resource.endpoint && (
                              <div className="text-xs text-gray-600 truncate" title={resource.endpoint}>
                                <span className="text-gray-500">Endpoint:</span>{' '}
                                <span className="font-medium">
                                  {resource.endpoint.length > 30 
                                    ? resource.endpoint.substring(0, 30) + '...' 
                                    : resource.endpoint}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircleIcon className={`h-6 w-6 text-${color}-600 ml-2 flex-shrink-0`} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResourceSelector;